import React from 'react';
import axios from 'axios';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import wurthTheme from './theme/wurthTheme';

// Import new SaaS components
import LoginPage from './components/auth/LoginPage';
import BusinessProfileDetail from './components/business/BusinessProfileDetail';
import OptimizationRecommendationsV2 from './components/optimization/OptimizationRecommendationsV2';
import PostsManager from './components/posts/PostsManager';
import SettingsPage from './components/settings/SettingsPage';
import ReviewsManager from './components/reviews/ReviewsManager';
import ReviewsPage from './components/reviews/ReviewsPage';
import RankingDashboard from './components/ranking/RankingDashboard';
import InsightsPage from './components/insights/InsightsPage';
import SuggestionPage from './components/suggestions/SuggestionPage';
import AgencyDashboard from './components/agency/AgencyDashboard';
import AgencyOverview from './components/agency/AgencyOverview';
import TopPerformerPage from './components/performance/TopPerformerPage';
import KeywordPerformancePage from './components/performance/KeywordPerformancePage';
import AllBusinessesPage from './components/performance/AllBusinessesPage';
import AllBusinessProfilesPage from './components/business/AllBusinessProfilesPage';
import PerformanceDetailsPage from './components/performance/PerformanceDetailsPage';
import ServiceManager from './components/ServiceManager';
import Layout from './components/layout/Layout';
import MediaManager from './components/media/MediaManager';
import LibraryPage from './components/library/LibraryPage';

// Import auth pages
import AuthSuccess from './pages/AuthSuccess';
import AuthError from './pages/AuthError';
import ProfilePage from './pages/ProfilePage';
import ReportPage from './pages/ReportPage';
import AdminUsersPage from './pages/AdminUsersPage';

// Auth context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BusinessProvider } from './contexts/BusinessContext';
import { BusinessSearchProvider } from './contexts/BusinessSearchContext';
import GMBActivity from './components/Activity/GmbActivityPage';

// Using Würth brand theme
const theme = wurthTheme;

// Configure axios defaults - Enhanced for Vercel deployment
const getApiBaseUrl = () => {
  // Produktionsumgebung auf Vercel
  if (process.env.NODE_ENV === 'production') {
    // Verwende aktuelle Domain für API-Aufrufe
    return window.location.origin;
  }
  // Lokale Entwicklung
  return 'http://localhost:5001';
};

const baseUrl = getApiBaseUrl();
axios.defaults.baseURL = baseUrl;

// Debug logging für Environment-Erkennung
console.log('🔧 App Environment Debug:');
console.log('  - NODE_ENV:', process.env.NODE_ENV);
console.log('  - Current Origin:', window.location.origin);
console.log('  - Axios Base URL:', baseUrl);

// Add token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - with production-specific handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log('🔴 Axios interceptor: HTTP error', {
      status: error.response?.status,
      url: error.config?.url,
      environment: process.env.NODE_ENV
    });
    
    if (error.response?.status === 401) {
      console.log('🔴 Axios interceptor: 401 Unauthorized - removing token and redirecting');
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading, authenticationInProgress } = useAuth();

  console.log('🛡️ ProtectedRoute: Route protection check', {
    loading,
    authenticationInProgress,
    hasUser: !!user,
    environment: process.env.NODE_ENV
  });

  // Show loading while auth is in progress or initially loading
  if (loading || authenticationInProgress) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {authenticationInProgress ? 'Authentifizierung läuft...' : 'Lädt...'}
        </Typography>
      </Box>
    );
  }

  if (!user) {
    console.log('🛡️ ProtectedRoute: No user found, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('✅ ProtectedRoute: User authenticated, allowing access');

  // Check if trial expired and no active subscription
  if (user.subscription?.status === 'trial' && new Date() > new Date(user.subscription?.trialEndDate)) {
    if (user.subscription?.status !== 'active') {
      return <Navigate to="/subscription" replace />;
    }
  }

  return children;
}

// Role-Protected Route Component
// Usage: <RoleProtectedRoute allowedRoles={['admin', 'member']}>{children}</RoleProtectedRoute>
function RoleProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading, authenticationInProgress, hasAnyRole } = useAuth();

  console.log('🛡️ RoleProtectedRoute: Role check', {
    allowedRoles,
    userRole: user?.role,
    hasAccess: hasAnyRole(allowedRoles)
  });

  // Show loading while auth is in progress
  if (loading || authenticationInProgress) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Authentifizierung läuft...
        </Typography>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  if (allowedRoles.length > 0 && !hasAnyRole(allowedRoles)) {
    console.log('❌ RoleProtectedRoute: Access denied - insufficient role');
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography variant="h5" gutterBottom>
          Zugriff verweigert
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ihre Benutzerrolle hat keinen Zugriff auf diese Seite.
        </Typography>
      </Box>
    );
  }

  return children;
}

// App Router Component
function AppRouter() {
  const { user, loading, authenticationInProgress } = useAuth();

  console.log('🧭 AppRouter: Routing check', {
    loading,
    authenticationInProgress,
    hasUser: !!user,
    currentPath: window.location.pathname
  });

  if (loading || authenticationInProgress) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {authenticationInProgress ? 'Authentifizierung läuft...' : 'Anwendung wird geladen...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={user ? <Navigate to="/agency-dashboard" replace /> : <LoginPage />}
      />
      
      {/* Auth callback routes */}
      <Route path="/auth-success" element={<AuthSuccess />} />
      <Route path="/auth/success" element={<AuthSuccess />} />
      <Route path="/auth-error" element={<AuthError />} />
      <Route path="/auth/error" element={<AuthError />} />
      
      {/* Protected routes */}
      <Route path="/" element={<Navigate to="/agency-dashboard" replace />} />

      <Route
        path="/agency-dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <AgencyDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/agency-overview"
        element={
          <ProtectedRoute>
            <Layout>
              <AgencyOverview />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/business/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <BusinessProfileDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/activity/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <GMBActivity />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/report/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <ReportPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/optimization/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <OptimizationRecommendationsV2 />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/posts/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <PostsManager />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/reviews-manager/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <ReviewsManager />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/reviews/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <ReviewsManager />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/medias/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <MediaManager />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ranking/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <RankingDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/insights/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <InsightsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/suggestions/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <SuggestionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/top-performers"
        element={
          <ProtectedRoute>
            <Layout>
              <TopPerformerPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/keyword-performance"
        element={
          <ProtectedRoute>
            <Layout>
              <KeywordPerformancePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/all-businesses"
        element={
          <ProtectedRoute>
            <Layout>
              <AllBusinessesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/all-business-profiles"
        element={
          <ProtectedRoute>
            <Layout>
              <AllBusinessProfilesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/performance-details"
        element={
          <ProtectedRoute>
            <Layout>
              <PerformanceDetailsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/services/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <ServiceManager />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/library/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <LibraryPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <SettingsPage />
              </Layout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <AdminUsersPage />
              </Layout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile/:locationId"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/agency-dashboard" replace />} />
    </Routes>
  );
}

// Main App Component
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BusinessSearchProvider>
          <BusinessProvider>
            <AppRouter />
          </BusinessProvider>
        </BusinessSearchProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;